import { useState } from "react";
import { Download, FileText, RefreshCw, Search } from "lucide-react";

import { useAuth } from "#/context/AuthProvider";
import { leaveApi } from "../api";
import { AdjustBalanceModal } from "../components/AdjustBalanceModal";
import type { AdjustBalanceTarget } from "../components/AdjustBalanceModal";
import { LeaveBalancesTable } from "../components/LeaveBalancesTable";
import { LeaveBackButton } from "../components/LeaveBackButton";
import { LeaveError } from "../components/LeaveError";
import { LeaveLoading } from "../components/LeaveLoading";
import { LeavePageHeader } from "../components/LeavePageHeader";
import { LeavePagination } from "../components/LeavePagination";
import { useAsyncData, usePagination } from "../hooks";
import { exportToExcel, exportToPdf } from "../lib/exportUtils";
import type { LeaveBalanceMatrixResponse } from "../types";

export default function LeaveBalancesPage() {
  const { hasRole, hasPermission } = useAuth();
  const canAdjust = hasRole("SUPER_ADMIN") || hasPermission("HR_LEAVE_MANAGE");
  const [adjusting, setAdjusting] = useState<AdjustBalanceTarget | null>(null);

  const [year, setYear] = useState(new Date().getFullYear());
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const pagination = usePagination(0, 10);

  const { data, loading, error, reload } = useAsyncData(
    () => leaveApi.balanceMatrix(year, pagination.page, pagination.size, search.trim() || undefined),
    [year, pagination.page, pagination.size, search],
  );

  function handleYearChange(nextYear: number) {
    setYear(nextYear);
    pagination.reset();
  }

  function handleSearchChange(value: string) {
    setSearch(value);
    pagination.reset();
  }

  async function handleExportPdf() {
    if (!data) return;
    setExporting(true);
    try {
      const { headers, rows } = buildExportData(data);
      await exportToPdf({
        title: `Leave Balance Matrix — ${year}`,
        subtitle: `${data.totalElements} employees · Generated: ${new Date().toLocaleString()}`,
        headers,
        rows,
        filename: `leave-balances-${year}`,
      });
    } finally {
      setExporting(false);
    }
  }

  async function handleExportExcel() {
    if (!data) return;
    setExportingExcel(true);
    try {
      const { headers, rows } = buildExportData(data);
      await exportToExcel(`Leave Balance Matrix — ${year}`, headers, rows, `leave-balances-${year}`);
    } finally {
      setExportingExcel(false);
    }
  }

  return (
    <div className="space-y-5">
      <LeavePageHeader
        title="Employee Leave Balances"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <LeaveBackButton to="/hr/leave" label="Dashboard" />
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={exporting || !data}
              className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-bold hover:bg-slate-50 disabled:opacity-50"
              title="Export as PDF"
            >
              <FileText className="h-4 w-4 text-red-500" />
              PDF
            </button>
            <button
              type="button"
              onClick={handleExportExcel}
              disabled={exportingExcel || !data}
              className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-bold hover:bg-slate-50 disabled:opacity-50"
              title="Export as Excel"
            >
              <Download className="h-4 w-4 text-emerald-600" />
              Excel
            </button>
          </div>
        }
      />


      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border bg-white p-4 shadow-sm">
        <label className="space-y-1.5">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Leave Year</span>
          <input
            type="number"
            className="block w-28 rounded-xl border px-3 py-2 text-sm outline-none focus:border-emerald-500"
            value={year}
            min={2020}
            max={2100}
            onChange={(e) => handleYearChange(Number(e.target.value))}
          />
        </label>

        <label className="flex-1 space-y-1.5">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Search Employee</span>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              className="block w-full rounded-xl border py-2 pl-9 pr-4 text-sm outline-none focus:border-emerald-500"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search by name, number, department…"
            />
          </div>
        </label>

        <button
          type="button"
          onClick={reload}
          className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-bold hover:bg-slate-50"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {loading ? <LeaveLoading label="Loading leave balances…" /> : null}
      {error ? <LeaveError message={error} onRetry={reload} /> : null}

      {data ? (
        <>
          <LeaveBalancesTable data={data} onAdjust={canAdjust ? setAdjusting : undefined} />
          <LeavePagination
            page={{ ...data, page: data.page, size: data.size, totalElements: data.totalElements, totalPages: data.totalPages, content: data.rows }}
            onPageChange={pagination.setPage}
            onSizeChange={(s) => { pagination.setSize(s); pagination.reset(); }}
            sizes={[10, 20, 50, 100]}
          />
        </>
      ) : null}

      {adjusting ? (
        <AdjustBalanceModal
          target={adjusting}
          onClose={() => setAdjusting(null)}
          onSaved={async () => {
            setAdjusting(null);
            await reload();
          }}
        />
      ) : null}
    </div>
  );
}

// ─── export helpers ───────────────────────────────────────────────────────────

function buildExportData(data: LeaveBalanceMatrixResponse) {
  const leaveTypeHeaders = data.columns.map((c) => c.leaveTypeName);
  const headers = [
    "Employee No.",
    "Employee Name",
    "Department",
    "Job Title",
    ...leaveTypeHeaders.flatMap((n) => [`${n} — Entitled`, `${n} — Used`, `${n} — Remaining`]),
  ];

  const rows = data.rows.map((row) => [
    row.employeeNumber,
    row.employeeName,
    row.departmentName ?? "",
    row.jobTitle ?? "",
    ...data.columns.flatMap((col) => {
      const cell = row.cells.find((c) => c.leaveTypeId === col.leaveTypeId);
      if (!cell || !cell.applicable) return ["N/A", "N/A", "N/A"];
      return [
        cell.entitledDays ?? 0,
        cell.usedDays ?? 0,
        cell.remainingDays ?? 0,
      ];
    }),
  ]);

  return { headers, rows };
}
